import { createContext, useContext, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import { AccountApi } from "../api";
import { isNullOrEmptyString, isNullOrUndefined } from "../helpers";
import { LocalStorageService } from '../providers/local-storage-service';
import { AuthConstants } from '../providers/auth-constants';
import logger from 'debug';
import { isTokenExpiring } from "../helpers/token-helpers";
import { Mutex } from 'async-mutex';
import { NotificationService } from "../providers";

const AuthContext = createContext();
const debug = logger('login:useAuth');

const mutex = new Mutex();

export const tryRenewToken = async (user, setUser, navigate) => {
	await mutex.runExclusive(async () => {
		// double-check to make sure it didn't get renewed on a previous pass...
		let isExpiring = isTokenExpiring(user?.token);
		debug('attempting to renew token', isExpiring);
		if (!isExpiring)
			return;

		let apiResponse = await AccountApi.renewToken(user.token);
		if (!isNullOrUndefined(apiResponse.error)) {
			NotificationService.error('Authentication expired.');

			debug('token renewal failed');
			setUser(null);
			navigate('/login');
		}
		else {
			debug('token renewal succeed');

			let oldUser = Object.assign({}, user);
			debug('old user', oldUser);
			const { token, validTo } = apiResponse;

			setUser({ token, validTo, externalUser: oldUser.externalUser });
		}
	});
}

export const checkExpiry = (user, setUser, navigate) => {
	if (isNullOrEmptyString(user?.token))
		return;

	let isExpiring = isTokenExpiring(user?.token);
	debug('isExpiring fired at with result', Date.now(), isExpiring);

	if (isExpiring)
		tryRenewToken(user, setUser, navigate);
}

export const AuthProvider = ({ children }) => {
    const didMount = useRef(false);
	const [user, setUser] = useLocalStorage("user", null);
	const navigate = useNavigate();

	// initial check
	useEffect(() => {
        let isFirstPass = false;
        if (!didMount.current) {
            didMount.current = true;
            isFirstPass = true;
        }

		if (isFirstPass)
			checkExpiry(user, setUser, navigate);
	}, [user, setUser, navigate]);

	useEffect(() => {
		const interval = setInterval(() => {
			checkExpiry(user, setUser, navigate);
		}, (300 * 1000)); // every 5 minutes

		return () => clearInterval(interval);
	}, [user, setUser, navigate]);

	// call this function when you want to authenticate the user
	const login = async (data) => {

		const { userName, password, rememberMe } = data;
		let loginResponse = await AccountApi.login({ userName, password });
		if (!isNullOrUndefined(loginResponse.token)) {
			setUser({ token: loginResponse.token, validTo: loginResponse.validTo, externalUser: loginResponse.user });
			if (rememberMe && rememberMe) {
				LocalStorageService.saveToLocalStorage(AuthConstants.UserName_LocalStorage_Key, userName);
			}
			navigate('/');
		}
		else {
			debug('login: ', loginResponse);
			return loginResponse;
		}
	};

	// call this function to sign out logged in user
	const logout = () => {
		setUser(null);
		navigate("/", { replace: true });
	};

	const value = useMemo(
		() => ({
			user,
			login,
			logout
		}),
		[user]
	);
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	return useContext(AuthContext);
};